import React from "react";
import Logo from "./Logo";
import {
  FaBell,
  FaChartBar,
  FaFileCsv,
  FaMagnifyingGlass,
  FaPaste,
  FaRightFromBracket,
  FaTrash,
} from "react-icons/fa6";

export default function Header({
  tab,
  setTab,
  stats,
  globalLoading,
  streamingCount,
  searchError,
  searchStatus,
  notificationsOpen,
  toggleNotifications,
  notificationsRef,
  leadNotifications,
  currentUser,
  moreMenuOpen,
  setMoreMenuOpen,
  moreMenuRef,
  exportCSV,
  setConfirmModal,
  clearAllLeads,
  signOut,
}) {
  const unreadCount = leadNotifications.filter((item) => !item.read).length;

  const statusType = searchError
    ? "error"
    : globalLoading
      ? "searching"
      : stats.total > 0
        ? "ready"
        : "idle";

  const statusText = searchError
    ? "Search issue"
    : globalLoading
      ? searchStatus || "Searching leads..."
      : stats.total > 0
        ? "Pipeline ready"
        : "Ready";

  return (
    <header className="wp-header">
      <div className="wp-header-glow" aria-hidden="true" />

      <div className="wp-header-left">
        <button
          type="button"
          className="wp-brand wp-brand-image-button"
          onClick={() => setTab("find")}
          aria-label="Open WaveProspect lead search"
        >
          <Logo className="wp-brand-logo" />
        </button>
      </div>

      <nav className="wp-header-nav" aria-label="Primary navigation">
        <button
          type="button"
          className={`wp-nav-btn ${tab === "find" ? "active" : ""}`}
          onClick={() => setTab("find")}
        >
          <FaMagnifyingGlass />
          <span className="wp-nav-label">Find Leads</span>
        </button>

        <button
          type="button"
          className={`wp-nav-btn ${tab === "paste" ? "active" : ""}`}
          onClick={() => setTab("paste")}
        >
          <FaPaste />
          <span className="wp-nav-label">Import</span>
        </button>

        <button
          type="button"
          className={`wp-nav-btn ${tab === "pipeline" ? "active" : ""}`}
          onClick={() => setTab("pipeline")}
        >
          <FaChartBar />
          <span className="wp-nav-label">Pipeline</span>

          {stats.total > 0 && (
            <span className="wp-nav-badge">
              {stats.total > 999 ? "999+" : stats.total}
            </span>
          )}
        </button>
      </nav>

      <div className="wp-header-right">
        {globalLoading && (
          <div className="wp-streaming-pill" aria-live="polite">
            <span className="wp-streaming-spinner" />
            <span className="wp-streaming-text">
              Streaming {streamingCount} leads
            </span>
          </div>
        )}

        <div
          className={`wp-header-status ${statusType}`}
          aria-live="polite"
          title={statusText}
        >
          <span className="wp-header-status-dot" />
          <span className="wp-header-status-text">{statusText}</span>
        </div>

        <div className="notifications-wrap" ref={notificationsRef}>
          <button
            type="button"
            className="wp-icon-button"
            onClick={toggleNotifications}
            aria-label="Notifications"
            title="Notifications"
          >
            <FaBell />

            {unreadCount > 0 && (
              <span className="bell-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {notificationsOpen && (
            <div className="notifications-popover">
              <div className="notifications-title">
                <span>Lead notifications</span>
                {unreadCount > 0 && (
                  <span className="wp-popover-count">{unreadCount}</span>
                )}
              </div>

              {leadNotifications.length === 0 ? (
                <div className="notifications-empty">
                  No lead notifications yet.
                </div>
              ) : (
                <div className="notifications-list">
                  {leadNotifications.map((item) => (
                    <div key={item.id} className="notification-item">
                      <div className="notification-main">{item.text}</div>

                      <div className="notification-sub">
                        {item.sourceLabel || "Leads"} ·{" "}
                        {item.countries || "Global"} ·{" "}
                        {new Date(item.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="account-menu-wrap" ref={moreMenuRef}>
          <button
            type="button"
            className={`account-trigger ${moreMenuOpen ? "open" : ""}`}
            onClick={() => setMoreMenuOpen((previous) => !previous)}
            aria-label="Account menu"
            title={currentUser?.name || "Account"}
          >
            {currentUser?.picture ? (
              <img
                src={currentUser.picture}
                alt=""
                className="account-avatar-image"
              />
            ) : (
              <span className="account-avatar-fallback">
                {currentUser?.name?.[0]?.toUpperCase() || "U"}
              </span>
            )}
          </button>

          {moreMenuOpen && (
            <div className="account-popover">
              <div className="account-popover-header">
                {currentUser?.picture ? (
                  <img
                    src={currentUser.picture}
                    alt=""
                    className="account-popover-avatar"
                  />
                ) : (
                  <span className="account-popover-avatar account-avatar-fallback">
                    {currentUser?.name?.[0]?.toUpperCase() || "U"}
                  </span>
                )}
                <div className="account-popover-identity">
                  <div className="account-popover-name">
                    {currentUser?.name || "Account"}
                  </div>
                  {currentUser?.email && (
                    <div className="account-popover-email">
                      {currentUser.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="wp-menu-divider" />

              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  exportCSV();
                  setMoreMenuOpen(false);
                }}
              >
                <FaFileCsv />
                <span>Export CSV</span>
              </button>

              <button
                type="button"
                className="menu-item menu-item-danger"
                disabled={stats.total === 0}
                onClick={() => {
                  setConfirmModal({
                    message: "Clear all leads?",
                    onConfirm: clearAllLeads,
                  });
                  setMoreMenuOpen(false);
                }}
              >
                <FaTrash />
                <span>Clear all leads</span>
              </button>

              <div className="wp-menu-divider" />

              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  signOut();
                  setMoreMenuOpen(false);
                }}
              >
                <FaRightFromBracket />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}


